import { useEffect, useMemo } from 'react'
import { useDebounce } from 'usehooks-ts'
import { useTranslation } from 'react-i18next'

import { AsyncSelect } from '@percent/lemonade'
import { OrganisationSelectProps } from './OrganisationSelect.types'
import { OrganisationPreview } from './OrganisationPreview/OrganisationPreview'
import { useQuery } from '@percent/education-hosted-form/hooks/useQuery/useQuery'
import { searchOrganisationsAction } from '@percent/education-hosted-form/api/organisations/organisations.actions'
import { StageTag } from '@percent/education-hosted-form/api/organisations/organisations.types'
import {
  FormState,
  SelectOption
} from '@percent/education-hosted-form/context/educationHostedForm/EducationHostedFormContext.types'
import { useEducationHostedForm } from '@percent/education-hosted-form/hooks/useEducationHostedForm/useEducationHostedForm'

const ORGANISATION_SEARCH_PAGE_SIZE = 10

const MIN_SEARCH_LENGTH = 3

export function OrganisationSelect({
  name,
  setOrganisationValue,
  setQueryValue,
  onChange,
  onAddOrganisation,
  countryCode,
  placeholder,
  query = '',
  organisation: selectedOrganisation
}: OrganisationSelectProps) {
  const { setFormState, partnerConfig } = useEducationHostedForm()
  const { t } = useTranslation()
  const debouncedQuery = useDebounce(query, 500)

  // Map the config's allowed education stages to the `gs.stage:*` tags org search filters on
  // (e.g. ['higher'] → ['gs.stage:higher']). Empty/absent → no stage restriction (all education orgs).
  const allowedStages = partnerConfig?.configuration.education?.allowedStages ?? []
  const stageTags: StageTag[] = allowedStages.map((stage): StageTag => `gs.stage:${stage}`)

  const {
    data: searchData,
    isFetching,
    isError
  } = useQuery(
    [
      'searchOrganisations',
      {
        query: debouncedQuery,
        countryCode,
        pageSize: ORGANISATION_SEARCH_PAGE_SIZE,
        type: ['education'],
        // One comma-joined entry so the API ORs (instead of ANDs) the stages; omitted entirely when unrestricted.
        ...(stageTags.length ? { tags: [stageTags.join(',')] } : {})
      }
    ],
    searchOrganisationsAction,
    { enabled: !!countryCode }
  )

  useEffect(() => {
    if (isError) {
      setFormState(FormState.ERROR)
    }
  }, [isError, setFormState])

  const selectOptions: SelectOption[] = useMemo(
    () =>
      searchData?.data.map(org => ({
        value: org.id,
        label: org.name,
        description: [org.address, org.postcode].filter(Boolean).join(', ')
      })) ?? [],
    [searchData]
  )

  const onOptionSelected = (organisation: SelectOption | null) => {
    onChange({ value: organisation?.value ?? '', label: organisation?.label ?? '' })
    setOrganisationValue(searchData?.data.find(org => org.id === organisation?.value))
  }

  return (
    <>
      <AsyncSelect
        name={name}
        key={countryCode}
        placeholder={placeholder}
        onChange={onOptionSelected}
        defaultValue={{ value: selectedOrganisation?.id ?? '', label: selectedOrganisation?.name ?? '' }}
        disabled={!countryCode}
        options={selectOptions}
        setQuery={setQueryValue}
        query={selectedOrganisation?.name ?? query}
        loading={query.length > 0 && isFetching && !selectedOrganisation}
        loadingText={t('typography.searching')}
        noResultsFoundText={t(query ? 'typography.noResultsFound' : 'typography.pleaseEnterSearchTerm')}
        data-testid="organisationSearch"
        minSearchLength={MIN_SEARCH_LENGTH}
        minSearchLengthMessage={t('typography.minSearchLengthMessage', { count: MIN_SEARCH_LENGTH })}
        addOption={{
          label: t('typography.cantFindMyOrg'),
          onClick: onAddOrganisation
        }}
      />
      {selectedOrganisation && <OrganisationPreview organisation={selectedOrganisation} />}
    </>
  )
}
